import { useEffect, useMemo, useRef, useState } from 'react'
import { contractTemplateApi } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplate.api'
import type { ContractConstructorData } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateConstructor.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  applyAmountWordsAutofill,
  buildConstructorRequestParams,
  buildValuesFromGroups,
  getAmountWordsFieldCode,
  getConstructorCalculatorFields,
  getConstructorGroupFieldCodes,
  getProductOptionKey,
  getRublesAmountInWords,
  getSelectedProductKey,
  hasSelectableProducts,
  mergeConstructorGroupValues,
  PARENT_GROUP_CODES,
  PRODUCT_GROUP_CODES,
} from '../utils/contractTemplateConstructor.utils'
import { getErrorText } from '../utils/getErrorText'
import { useCalculatorRecalculation } from './useCalculatorRecalculation'

type LoadConstructorArgs = {
  nextTemplateId: number
  nextParentId?: number | null
  nextProductKey?: string
  includeProductParams?: boolean
  loadingScope?: 'constructor' | 'parent' | 'product'
}

type UseTemplateConstructorParams = {
  active: boolean
  userId?: number | string | null
  /** Для конструктора в карточке лида (без ЛК или вместе с ЛК). */
  leadId?: number | string | null
}

export const useTemplateConstructor = ({
  active,
  userId,
  leadId,
}: UseTemplateConstructorParams) => {
  const addAlert = useAddAlert()
  const addAlertRef = useRef(addAlert)

  useEffect(() => {
    addAlertRef.current = addAlert
  }, [addAlert])

  const [constructorLoading, setConstructorLoading] = useState(false)
  const [parentLoading, setParentLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [constructorData, setConstructorData] =
    useState<ContractConstructorData | null>(null)
  const [documentName, setDocumentName] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null)
  const [selectedProductKey, setSelectedProductKey] = useState('custom')
  const [invalidFieldCodes, setInvalidFieldCodes] = useState<Set<string>>(
    new Set(),
  )

  const calculatorFields = useMemo(
    () => getConstructorCalculatorFields(constructorData?.groups),
    [constructorData?.groups],
  )

  const {
    recalculatingCodes,
    calculatorCodes,
    scheduleRecalculation,
    cancelRecalculation,
  } = useCalculatorRecalculation({
    calculatorFields,
    setValues,
  })

  const parentOptions = useMemo(
    () =>
      constructorData?.parents?.map((parent) => ({
        label: parent.name,
        value: parent.id == null ? null : String(parent.id),
      })) ?? [],
    [constructorData?.parents],
  )

  const productOptions = useMemo(
    () =>
      constructorData?.products?.map((product) => ({
        label: product.title,
        value: getProductOptionKey(product),
      })) ?? [],
    [constructorData?.products],
  )

  const shouldShowProductSelector = hasSelectableProducts(
    constructorData?.products,
  )
  const constructorGroups = constructorData?.groups ?? []

  const loadConstructor = async ({
    nextTemplateId,
    nextParentId,
    nextProductKey = 'custom',
    includeProductParams = false,
    loadingScope = 'constructor',
  }: LoadConstructorArgs) => {
    const resolvedUserId =
      userId != null && Number.isFinite(Number(userId)) && Number(userId) > 0
        ? Number(userId)
        : undefined
    const resolvedLeadId =
      leadId != null && Number.isFinite(Number(leadId)) && Number(leadId) > 0
        ? Number(leadId)
        : undefined

    if (!resolvedUserId && !resolvedLeadId) return

    cancelRecalculation()

    if (loadingScope === 'parent') {
      setParentLoading(true)
    } else if (loadingScope === 'product') {
      setProductLoading(true)
    } else {
      setConstructorLoading(true)
    }

    try {
      const data = await contractTemplateApi.getConstructor(
        buildConstructorRequestParams({
          template_id: Number(nextTemplateId),
          user_id: resolvedUserId,
          lead_id: resolvedLeadId,
          parent_id: nextParentId,
          productKey: nextProductKey,
          includeProductParams,
        }),
      )

      const nextValues = applyAmountWordsAutofill(
        buildValuesFromGroups(data.groups),
      )

      setConstructorData(data)

      if (loadingScope === 'parent') {
        const parentFieldCodes = getConstructorGroupFieldCodes(
          data.groups,
          PARENT_GROUP_CODES,
        )
        setValues((prev) =>
          mergeConstructorGroupValues(prev, nextValues, parentFieldCodes),
        )
        setInvalidFieldCodes((prev) => {
          if (!prev.size) return prev
          const next = new Set(prev)
          parentFieldCodes.forEach((code) => next.delete(code))
          return next
        })
      } else if (loadingScope === 'product') {
        const productFieldCodes = getConstructorGroupFieldCodes(
          data.groups,
          PRODUCT_GROUP_CODES,
        )
        setValues((prev) =>
          mergeConstructorGroupValues(prev, nextValues, productFieldCodes),
        )
        setInvalidFieldCodes((prev) => {
          if (!prev.size) return prev
          const next = new Set(prev)
          productFieldCodes.forEach((code) => next.delete(code))
          return next
        })
      } else {
        setDocumentName(data.document_name || '')
        setValues(nextValues)
        setSelectedParentId(data.selected_parent_id ?? null)
        setSelectedProductKey(getSelectedProductKey(data.selected_product))
        setInvalidFieldCodes(new Set())
      }
    } catch (error) {
      addAlertRef.current({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось загрузить поля шаблона'),
        type: 'error',
      })
    } finally {
      if (loadingScope === 'parent') {
        setParentLoading(false)
      } else if (loadingScope === 'product') {
        setProductLoading(false)
      } else {
        setConstructorLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!active || templateId == null) return

    loadConstructor({
      nextTemplateId: templateId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, templateId])

  const handleTemplateChange = (value: string | string[] | null) => {
    const rawValue = Array.isArray(value) ? value[0] ?? null : value
    const nextTemplateId =
      rawValue != null && rawValue !== '' ? Number(rawValue) : null

    setTemplateId(nextTemplateId)
    setConstructorData(null)
    setValues({})
    setSelectedParentId(null)
    setSelectedProductKey('custom')
    setInvalidFieldCodes(new Set())
  }

  const handleParentChange = (parentId: number | null) => {
    if (templateId == null) return

    setSelectedParentId(parentId)
    loadConstructor({
      nextTemplateId: templateId,
      nextParentId: parentId,
      nextProductKey: selectedProductKey,
      includeProductParams: true,
      loadingScope: 'parent',
    })
  }

  const handleProductChange = (productKey: string) => {
    if (templateId == null) return

    setSelectedProductKey(productKey)
    loadConstructor({
      nextTemplateId: templateId,
      nextParentId: selectedParentId,
      nextProductKey: productKey,
      includeProductParams: true,
      loadingScope: 'product',
    })
  }

  const handleFieldChange = (code: string, value: string) => {
    const amountWordsFieldCode = getAmountWordsFieldCode(code)
    const amountWords = amountWordsFieldCode ? getRublesAmountInWords(value) : ''

    setValues((prev) => {
      const nextValues = {
        ...prev,
        [code]: value,
        ...(amountWordsFieldCode
          ? { [amountWordsFieldCode]: amountWords }
          : {}),
      }

      if (!calculatorCodes.has(code)) {
        scheduleRecalculation(nextValues)
      }

      return nextValues
    })

    setInvalidFieldCodes((prev) => {
      if (
        !prev.has(code) &&
        (!amountWordsFieldCode || !prev.has(amountWordsFieldCode))
      ) {
        return prev
      }

      const next = new Set(prev)
      next.delete(code)
      if (amountWords && amountWordsFieldCode) {
        next.delete(amountWordsFieldCode)
      }
      return next
    })
  }

  return {
    templateId,
    constructorData,
    constructorGroups,
    constructorLoading,
    parentLoading,
    productLoading,
    documentName,
    setDocumentName,
    values,
    selectedParentId,
    selectedProductKey,
    invalidFieldCodes,
    setInvalidFieldCodes,
    recalculatingCodes,
    parentOptions,
    productOptions,
    shouldShowProductSelector,
    handleTemplateChange,
    handleParentChange,
    handleProductChange,
    handleFieldChange,
  }
}
